import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, ChevronRight, Check, Loader2, Pencil } from 'lucide-react';
import LocationPicker from './LocationPicker';
import { regionLabel } from '../lib/regionNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Place types that are "city-level" and therefore warrant an optional
// microdistrict refinement. A village / district / parish is already precise
// enough, so we never push an extra refinement step for those.
const CITY_TYPES = new Set(['city', 'municipality']);

/**
 * KoLV unified location chooser. One guided field: search a place once and the
 * novads (region) is derived automatically. The current choice is shown as a
 * clear "maršruts" breadcrumb (novads › pilsēta › rajons), with an optional,
 * recommended refinement to a microdistrict when the chosen city has them.
 *
 * value: { region, location_name, location_region_slug, location_district,
 *          location_district_slug, lat, lng }
 * onChange(partial): merged into the parent's form state.
 */
export default function LocationField({ value = {}, onChange, testid = 'location-field' }) {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  const [regionName, setRegionName] = useState(regionLabel(value.region));
  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [editing, setEditing] = useState(false);

  const hasSelection = !!(value.location_name || value.region);

  const loadDistricts = useCallback(async (lat, lng) => {
    if (lat == null || lng == null) { setDistricts([]); return; }
    setLoadingDistricts(true);
    try {
      const { data } = await axios.get(`${API}/locations/districts-near`, { params: { lat, lng, radius_km: 12 } });
      setDistricts(data.districts || []);
    } catch (e) { setDistricts([]); } finally { setLoadingDistricts(false); }
  }, []);

  // Resolve the novads display name + load refinements for an already-saved
  // location (once per unique region/point).
  const resolvedRef = useRef(null);
  useEffect(() => {
    const key = `${value.region || ''}|${value.lat ?? ''},${value.lng ?? ''}`;
    if (!hasSelection || resolvedRef.current === key) return;
    resolvedRef.current = key;

    if (value.region) {
      const fallback = regionLabel(value.region);
      setRegionName(fallback);
      if (!fallback) {
        axios.get(`${API}/locations/resolve-region`, { params: { slug: value.region } })
          .then(({ data }) => { if (data.city_name) setRegionName(data.city_name); })
          .catch(() => {});
      }
    }
    if (value.lat != null && value.lng != null) loadDistricts(value.lat, value.lng);
  }, [value.region, value.lat, value.lng, hasSelection, loadDistricts]);

  const handlePick = useCallback(async (l) => {
    const next = {
      location_id: l.id || '',
      location_name: l.name,
      location_region_slug: l.region_slug || value.location_region_slug || '',
      location_district: '',
      location_district_slug: '',
      lat: l.lat,
      lng: l.lng,
      region: value.region || '',
    };
    setEditing(false);
    try {
      const { data } = await axios.get(`${API}/locations/resolve-region`, { params: { id: l.id, slug: l.slug } });
      if (data.city_slug) {
        next.region = data.city_slug;
        setRegionName(data.city_name || regionLabel(data.city_slug));
      } else {
        next.region = l.region_slug || '';
        setRegionName('');
      }
      if (CITY_TYPES.has(l.type)) {
        loadDistricts(data.city_lat ?? l.lat, data.city_lng ?? l.lng);
      } else {
        setDistricts([]);
      }
    } catch (e) {
      next.region = l.region_slug || value.region || '';
    }
    if (onChange) onChange(next);
  }, [value.region, value.location_region_slug, onChange, loadDistricts]);

  const pickDistrict = (d) => {
    const same = value.location_district_slug === d.slug;
    if (onChange) onChange({
      location_district: same ? '' : d.name,
      location_district_slug: same ? '' : d.slug,
    });
  };
  const clearDistrict = () => { if (onChange) onChange({ location_district: '', location_district_slug: '' }); };

  // Breadcrumb ("maršruts") — unique chain, never repeat the city.
  const segs = [];
  if (regionName) segs.push(regionName);
  if (value.location_name && value.location_name !== regionName) segs.push(value.location_name);
  if (value.location_district) segs.push(value.location_district);

  const showPicker = !hasSelection || editing;
  const showRefine = !editing && districts.length > 0;

  return (
    <div className="space-y-3" data-testid={testid}>
      {/* Current choice as a route breadcrumb */}
      {hasSelection && !editing && segs.length > 0 && (
        <div className="rounded-xl bg-[#4A6B53]/5 border border-[#4A6B53]/20 p-4" data-testid={`${testid}-breadcrumb`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center flex-wrap gap-1.5 min-w-0">
              <MapPin className="w-4 h-4 text-[#4A6B53] shrink-0" />
              {segs.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />}
                  <span className={`text-sm ${i === 0 ? 'font-semibold text-[#121212]' : 'text-[#5C5C5C]'}`} data-testid={`${testid}-seg-${i}`}>{s}</span>
                </React.Fragment>
              ))}
            </div>
            <button type="button" onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#D97736] hover:underline shrink-0"
              data-testid={`${testid}-change`}>
              <Pencil className="w-3.5 h-3.5" /> {tr('Mainīt', 'Change', 'Изменить')}
            </button>
          </div>
        </div>
      )}

      {/* The single guided search field */}
      {showPicker && (
        <div className="space-y-2">
          <LocationPicker
            value={editing ? '' : value.location_name}
            onSelect={handlePick}
            placeholder={tr('Meklē pilsētu, ciemu vai rajonu...', 'Search city, village or district...', 'Поиск города, села или района...')}
            testid={`${testid}-picker`}
          />
          {hasSelection && editing && (
            <button type="button" onClick={() => setEditing(false)}
              className="text-xs text-[#5C5C5C] hover:text-[#121212]" data-testid={`${testid}-cancel`}>
              {tr('Atcelt', 'Cancel', 'Отмена')}
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-[#5C5C5C]">
        {tr('Novads tiek noteikts automātiski no izvēlētās vietas. Izmanto, lai redzētu vietējos katalogus un saņemtu paziņojumus par tuvumā esošiem darbiem.',
          'Your region is detected automatically from the chosen place. Used to show local catalogs and notify you about nearby jobs.',
          'Край определяется автоматически по выбранному месту. Используется для показа местных каталогов и уведомлений о работах поблизости.')}
      </p>

      {/* Optional, recommended refinement to a microdistrict */}
      {showRefine && (
        <div className="space-y-2 pt-1" data-testid={`${testid}-refine`}>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-[#121212]">
              {tr('Precizē rajonu (ieteicams)', 'Refine the district (recommended)', 'Уточните район (рекомендуется)')}
            </p>
            {loadingDistricts && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D97736]" />}
          </div>
          <p className="text-xs text-[#5C5C5C]">
            {tr('Palīdz tuvumā esošajiem atrast tevi precīzāk.', 'Helps nearby people find you more precisely.', 'Помогает людям рядом найти вас точнее.')}
          </p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            <button type="button" onClick={clearDistrict}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${!value.location_district_slug ? 'bg-[#4A6B53] text-white border-[#4A6B53]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
              data-testid={`${testid}-district-none`}>
              {!value.location_district_slug && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
              {tr('Visa pilsēta', 'Whole city', 'Весь город')}
            </button>
            {districts.map((d) => {
              const on = value.location_district_slug === d.slug;
              return (
                <button key={d.slug} type="button" onClick={() => pickDistrict(d)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-[#D97736] text-white border-[#D97736]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
                  data-testid={`${testid}-district-${d.slug}`}>
                  {on && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                  {d.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
